<script lang="ts">
  import Icon from './Icon.svelte';
  import type { TripCityView } from '~/shared/api.js';

  let {
    cities,
    activeCityId,
    activeLocality = null,
    editable,
    onActivate,
    onClearLocality,
    onPromoteLocality,
    onAdd,
    onManage,
  }: {
    cities: TripCityView[];
    activeCityId: number | null;
    /** Locality string set when the user picks a locality-derived auto-city
     *  from the Auto view (e.g. "Nagoya" with no formalized trip city).
     *  Mutually exclusive with activeCityId. */
    activeLocality?: string | null;
    editable: boolean;
    onActivate: (cityId: number | null) => void;
    onClearLocality?: () => void;
    /** Promote the active locality to a real trip city via the AddCity
     *  flow (locality name pre-filled). Only meaningful for editable trips. */
    onPromoteLocality?: () => void;
    onAdd: () => void;
    /** Open the manage-cities modal. Replaces the per-chip × — keeps
     *  chips pure selectors (no accidental deletes). */
    onManage?: () => void;
  } = $props();

  const hasActive = $derived(
    activeLocality !== null ||
      (activeCityId !== null && cities.some((c) => c.id === activeCityId)),
  );
  const rowLabel = $derived(
    hasActive
      ? editable
        ? 'Searching in:'
        : 'Filtering by:'
      : editable
        ? 'Tap a city to focus search'
        : 'Tap a city to filter',
  );
  const rowIcon = $derived(hasActive && editable ? 'search' : 'map-pin');
</script>

{#if cities.length > 0 || editable || activeLocality !== null}
  <nav class="cities-row" aria-label="Trip cities">
    {#if cities.length > 0 || activeLocality !== null}
      <span class="row-label" aria-hidden="true">
        <Icon name={rowIcon} size={12} strokeWidth={2.25} />
        <span>{rowLabel}</span>
      </span>
    {/if}
    {#if activeLocality !== null}
      <!-- Locality-derived city focus (set from Auto view; not a real trip
           city). Dashed border + sparkles glyph signals "computed slice."
           Two actions: + promote to a real trip city, × clear the focus. -->
      <span class="city-chip city-chip--locality active">
        <span class="city-name">
          <Icon name="sparkles" size={11} strokeWidth={2.25} />
          <span>{activeLocality}</span>
        </span>
        {#if editable && onPromoteLocality}
          <button
            type="button"
            class="city-icon-btn"
            aria-label="Add {activeLocality} to trip cities"
            title="Add {activeLocality} to trip cities"
            onclick={() => onPromoteLocality?.()}
          >
            <Icon name="plus" size={11} strokeWidth={2.25} />
          </button>
        {/if}
        <button
          type="button"
          class="city-icon-btn"
          aria-label="Clear locality filter"
          title="Clear filter"
          onclick={() => onClearLocality?.()}
        >
          <Icon name="x" size={11} strokeWidth={2.25} />
        </button>
      </span>
    {/if}
    {#each cities as c (c.id)}
      {@const active = activeCityId === c.id}
      <span class="city-chip city-chip--no-trail" class:active>
        <button
          type="button"
          class="city-name"
          aria-pressed={active}
          title={active
            ? 'Search focuses here. Click to switch off.'
            : `Click to focus search on ${c.name}`}
          onclick={() => onActivate(active ? null : c.id)}
        >{c.name}</button>
      </span>
    {/each}
    {#if editable && cities.length === 0}
      <button type="button" class="add-city" onclick={onAdd}>
        <Icon name="plus" size={12} strokeWidth={2} />
        <span>Add a city to focus search</span>
      </button>
    {/if}
    {#if editable && cities.length > 0}
      <span class="add-city add-city--split">
        <button type="button" class="split-action" onclick={onAdd}>
          <Icon name="plus" size={12} strokeWidth={2} />
          <span>Add city</span>
        </button>
        {#if onManage}
          <span class="split-divider" aria-hidden="true"></span>
          <button
            type="button"
            class="split-action split-action--icon"
            aria-label="Manage cities"
            title="Manage cities"
            onclick={() => onManage?.()}
          >
            <Icon name="settings" size={12} strokeWidth={2} />
          </button>
        {/if}
      </span>
    {/if}
  </nav>
{/if}

<style>
  .cities-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  }
  .city-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    background: var(--color-canvas);
    color: var(--color-ink);
    border: 1px solid var(--color-hairline);
    border-radius: 9999px;
    padding-left: 0.625rem;
    padding-right: 0.25rem;
    height: 28px;
    font-size: 13px;
    font-weight: 500;
    line-height: 1;
  }
  .city-chip.active {
    background: var(--color-ink);
    color: var(--color-on-dark);
    border-color: var(--color-ink);
  }
  .city-name {
    background: transparent;
    border: 0;
    color: inherit;
    cursor: pointer;
    padding: 0;
    height: 100%;
    font: inherit;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  /* Symmetric padding when there's no trailing × button (view-only). */
  .city-chip--no-trail { padding-right: 0.625rem; }
  .city-chip--locality {
    padding-right: 0.25rem;
    border-style: dashed;
    border-color: var(--color-border-strong);
  }
  .city-chip--locality.active {
    background: var(--color-surface-soft);
    color: var(--color-ink);
    border-style: dashed;
    border-color: var(--color-border-strong);
  }
  .city-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 0;
    color: inherit;
    opacity: 0.6;
    cursor: pointer;
    width: 20px;
    height: 20px;
    border-radius: 9999px;
    margin-left: 2px;
  }
  .city-icon-btn:hover { opacity: 1; background: var(--color-surface-soft); }
  .add-city {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    background: transparent;
    color: var(--color-muted);
    border: 1px dashed var(--color-hairline);
    border-radius: 9999px;
    padding: 0 0.75rem;
    height: 28px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: color 100ms ease, border-color 100ms ease;
  }
  .add-city:hover { color: var(--color-ink); border-color: var(--color-border-strong); }
  /* Split: "+ Add city" and the gear share one outer pill divided
     by a 1px line. Same dashed-pill aesthetic as the standalone
     .add-city; each half is its own button. */
  .add-city.add-city--split {
    padding: 0;
    gap: 0;
    overflow: hidden;
  }
  .add-city--split .split-action {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    background: transparent;
    border: 0;
    padding: 0 0.75rem;
    height: 100%;
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    color: inherit;
    cursor: pointer;
  }
  .add-city--split .split-action:hover { color: var(--color-ink); }
  /* Icon-only half (the gear) deserves a square-ish footprint. */
  .add-city--split .split-action--icon {
    min-width: 36px;
    padding: 0 0.5rem;
    justify-content: center;
  }
  .add-city--split .split-divider {
    width: 1px;
    height: 60%;
    background: var(--color-hairline);
    flex: none;
  }
</style>
